import { useState, useEffect, useCallback } from 'react';
import { Shield, Home, Briefcase, MapPin, Loader2, Search, X, ShieldCheck, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { privacyZonesApi } from '../lib/api';

const ZONE_META = {
    home: { label: 'Home', Icon: Home, color: 'from-emerald-500 to-teal-500' },
    work: { label: 'Work', Icon: Briefcase, color: 'from-indigo-500 to-blue-500' },
};

function ZoneRow({ zoneKey, zone, radiusMeters, onSetGps, onSetAddress, onClear, busy }) {
    const meta = ZONE_META[zoneKey];
    const { Icon, label, color } = meta;
    const [mode, setMode] = useState(null); // null | 'gps' | 'search'
    const [query, setQuery] = useState('');
    const [searching, setSearching] = useState(false);

    const runSearch = async () => {
        if (!query.trim()) return;
        setSearching(true);
        try {
            // OpenStreetMap Nominatim — free, no API key required.
            // NOTE: do NOT send custom headers here (e.g. Accept-Language).
            // Any custom header upgrades this to a preflighted CORS request
            // which Nominatim rate-limits/refuses from browser origins.
            // Pass accept-language as a query param instead — Nominatim honors it.
            const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&accept-language=en&q=${encodeURIComponent(query)}`;
            const res = await fetch(url);
            const results = await res.json();
            if (!results || results.length === 0) {
                toast.error('No matches for that address');
                return;
            }
            const hit = results[0];
            await onSetAddress(zoneKey, {
                latitude: parseFloat(hit.lat),
                longitude: parseFloat(hit.lon),
                label: hit.display_name,
            });
            setQuery('');
            setMode(null);
        } catch (err) {
            console.error(err);
            toast.error('Address search failed');
        } finally {
            setSearching(false);
        }
    };

    return (
        <div className="border border-slate-200 rounded-2xl p-4" data-testid={`privacy-zone-${zoneKey}-row`}>
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${color} text-white flex items-center justify-center shrink-0`}>
                        <Icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                        <div className="font-semibold text-slate-800">{label}</div>
                        {zone ? (
                            <div className="text-xs text-slate-500 truncate" title={zone.label || ''}>
                                {zone.label || `${zone.latitude.toFixed(4)}, ${zone.longitude.toFixed(4)}`}
                            </div>
                        ) : (
                            <div className="text-xs text-slate-400">Not set</div>
                        )}
                    </div>
                </div>
                {zone && (
                    <button
                        type="button"
                        onClick={() => onClear(zoneKey)}
                        disabled={busy}
                        className="text-slate-400 hover:text-rose-500 disabled:opacity-50"
                        data-testid={`privacy-zone-${zoneKey}-clear`}
                        aria-label={`Clear ${label} zone`}
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            {mode === null && (
                <div className="flex gap-2 mt-3">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onSetGps(zoneKey)}
                        disabled={busy}
                        className="flex-1"
                        data-testid={`privacy-zone-${zoneKey}-gps-btn`}
                    >
                        <MapPin className="w-4 h-4 mr-1" />
                        {zone ? 'Update via GPS' : 'Use current location'}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setMode('search')}
                        disabled={busy}
                        className="flex-1"
                        data-testid={`privacy-zone-${zoneKey}-search-btn`}
                    >
                        <Search className="w-4 h-4 mr-1" />
                        Search address
                    </Button>
                </div>
            )}

            {mode === 'search' && (
                <div className="mt-3 flex gap-2">
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="e.g. 350 5th Ave, New York"
                        className="flex-1 text-sm"
                        onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                        data-testid={`privacy-zone-${zoneKey}-search-input`}
                    />
                    <Button
                        type="button"
                        size="sm"
                        onClick={runSearch}
                        disabled={searching || !query.trim()}
                        data-testid={`privacy-zone-${zoneKey}-search-submit`}
                    >
                        {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Find'}
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => { setMode(null); setQuery(''); }}
                    >
                        Cancel
                    </Button>
                </div>
            )}

            {zone && (
                <div className="mt-2 text-[11px] text-slate-400">
                    Radius: {radiusMeters}m around this point
                </div>
            )}
        </div>
    );
}

export default function PrivacyZones() {
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [radius, setRadius] = useState(200);
    const [home, setHome] = useState(null);
    const [work, setWork] = useState(null);
    const [statusZone, setStatusZone] = useState(null); // "home" | "work" | null — active zone right now
    const [checkingStatus, setCheckingStatus] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await privacyZonesApi.get();
            const d = res.data || {};
            setEnabled(Boolean(d.enabled));
            setRadius(d.radius_meters || 200);
            setHome(d.home || null);
            setWork(d.work || null);
        } catch (err) {
            console.error('Failed to load privacy zones', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const persist = async (payload, successMsg) => {
        setBusy(true);
        try {
            const res = await privacyZonesApi.update(payload);
            const d = res.data || {};
            setEnabled(Boolean(d.enabled));
            setRadius(d.radius_meters || 200);
            setHome(d.home || null);
            setWork(d.work || null);
            if (successMsg) toast.success(successMsg);
            return true;
        } catch (err) {
            console.error(err);
            toast.error('Could not save privacy zone');
            return false;
        } finally {
            setBusy(false);
        }
    };

    const toggleEnabled = async () => {
        const next = !enabled;
        setEnabled(next); // optimistic
        const ok = await persist({ enabled: next }, next ? 'Privacy Zones ON' : 'Privacy Zones OFF');
        if (!ok) setEnabled(!next);
    };

    const handleRadiusCommit = async (val) => {
        await persist({ radius_meters: val }, `Radius set to ${val}m`);
    };

    const handleSetGps = (zoneKey) => {
        if (!navigator.geolocation) {
            toast.error('GPS not available on this device');
            return;
        }
        setBusy(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                await persist(
                    {
                        [zoneKey]: {
                            latitude: pos.coords.latitude,
                            longitude: pos.coords.longitude,
                            label: 'Current location',
                        },
                    },
                    `${ZONE_META[zoneKey].label} saved`
                );
            },
            (err) => {
                console.error(err);
                toast.error(err.code === err.PERMISSION_DENIED
                    ? 'Location permission denied'
                    : 'Could not get GPS location');
                setBusy(false);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    };

    const handleSetAddress = async (zoneKey, coords) => {
        await persist({ [zoneKey]: coords }, `${ZONE_META[zoneKey].label} saved`);
    };

    const handleClear = async (zoneKey) => {
        const clearKey = zoneKey === 'home' ? 'clear_home' : 'clear_work';
        await persist({ [clearKey]: true }, `${ZONE_META[zoneKey].label} cleared`);
    };

    // Check whether current GPS position is inside a zone right now.
    const checkCurrent = () => {
        if (!navigator.geolocation) {
            toast.error('GPS not available on this device');
            return;
        }
        setCheckingStatus(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    const res = await privacyZonesApi.checkStatus(pos.coords.latitude, pos.coords.longitude);
                    const zone = res.data?.zone || null;
                    setStatusZone(zone);
                    if (zone) {
                        toast.success(`You're inside your ${zone} zone — location paused`);
                    } else {
                        toast.info(enabled ? 'Not currently in a privacy zone' : 'Privacy zones are off');
                    }
                } catch {
                    toast.error('Could not check status');
                } finally {
                    setCheckingStatus(false);
                }
            },
            () => {
                toast.error('Could not read GPS');
                setCheckingStatus(false);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-6 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" />
            </div>
        );
    }

    return (
        <div data-testid="privacy-zones-card">
            {/* Header + master toggle */}
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                        enabled ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'
                    }`}>
                        {enabled ? <ShieldCheck className="w-6 h-6" /> : <Shield className="w-6 h-6" />}
                    </div>
                    <div className="min-w-0">
                        <h3 className="font-bold text-slate-800 flex items-center gap-2">
                            Privacy Zones
                            {enabled && statusZone && (
                                <span className="text-[10px] font-semibold uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 rounded-full">
                                    Paused · {statusZone}
                                </span>
                            )}
                        </h3>
                        <p className="text-sm text-slate-500">
                            {enabled
                                ? 'Location recording pauses automatically when you\'re near Home or Work.'
                                : 'Turn on to stop recording your location near Home or Work.'}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    aria-label="Toggle Privacy Zones"
                    disabled={busy}
                    onClick={toggleEnabled}
                    data-testid="privacy-zones-toggle"
                    className={`shrink-0 mt-1 w-12 h-7 rounded-full transition-colors disabled:opacity-50 ${
                        enabled ? 'bg-emerald-600' : 'bg-slate-300'
                    }`}
                >
                    <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                        enabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                </button>
            </div>

            {/* Radius slider */}
            <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700" htmlFor="pz-radius">Radius</label>
                    <span className="text-sm font-semibold text-slate-800" data-testid="privacy-zones-radius-value">{radius}m</span>
                </div>
                <input
                    id="pz-radius"
                    type="range"
                    min={100}
                    max={500}
                    step={50}
                    value={radius}
                    onChange={(e) => setRadius(parseInt(e.target.value, 10))}
                    onMouseUp={(e) => handleRadiusCommit(parseInt(e.target.value, 10))}
                    onTouchEnd={(e) => handleRadiusCommit(parseInt(e.target.value, 10))}
                    disabled={busy}
                    className="w-full accent-emerald-600"
                    data-testid="privacy-zones-radius-slider"
                />
                <div className="flex justify-between text-[11px] text-slate-400 mt-1">
                    <span>100m</span>
                    <span>250m</span>
                    <span>500m</span>
                </div>
            </div>

            {/* Zone rows */}
            <div className="mt-5 space-y-3">
                <ZoneRow
                    zoneKey="home"
                    zone={home}
                    radiusMeters={radius}
                    onSetGps={handleSetGps}
                    onSetAddress={handleSetAddress}
                    onClear={handleClear}
                    busy={busy}
                />
                <ZoneRow
                    zoneKey="work"
                    zone={work}
                    radiusMeters={radius}
                    onSetGps={handleSetGps}
                    onSetAddress={handleSetAddress}
                    onClear={handleClear}
                    busy={busy}
                />
            </div>

            {/* Live status check */}
            <div className="mt-5 flex items-center justify-between gap-3 bg-slate-50 rounded-xl p-3">
                <div className="text-xs text-slate-600 flex items-center gap-2 min-w-0">
                    {enabled && statusZone ? (
                        <>
                            <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                            <span className="truncate">Currently inside your <b>{statusZone}</b> zone — nothing is being recorded.</span>
                        </>
                    ) : enabled ? (
                        <>
                            <Shield className="w-4 h-4 text-slate-500 shrink-0" />
                            <span>Zones are armed. Check where you are right now.</span>
                        </>
                    ) : (
                        <>
                            <ShieldOff className="w-4 h-4 text-slate-400 shrink-0" />
                            <span>Zones are off. Turn on the toggle above to activate.</span>
                        </>
                    )}
                </div>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={checkCurrent}
                    disabled={checkingStatus}
                    data-testid="privacy-zones-check-btn"
                >
                    {checkingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check now'}
                </Button>
            </div>
        </div>
    );
}
